import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useAuth } from '../Auth';
import { parseToken } from '../../utils/parseToken';
const tokenPropertiesInitialValue = {
    clientId: undefined,
    entities: [],
    entitySegments: {},
    permissions: [],
    sub: '',
    subEntityId: undefined,
    userId: '',
    userRoles: [],
    userType: 'External',
};
const initialValue = {
    actions: {},
    loading: false,
    tokenProperties: tokenPropertiesInitialValue,
};
export const ClientTokenContext = createContext(initialValue);
export const useClientTokenManager = () => {
    const context = useContext(ClientTokenContext);
    return context;
};
export const ClientTokenManagerProvider = ({ initialClientID, initialToken, children }) => {
    const { authService } = useAuth();
    const [clientID, setClientID] = useState(initialClientID);
    const [accessToken, setAccessToken] = useState(initialToken);
    const [loading, setLoading] = useState(false);
    const refreshToken = useCallback(async (newClientID) => {
        if (newClientID === clientID) {
            return;
        }
        const clientToRefresh = newClientID || clientID;
        if (!clientToRefresh) {
            // eslint-disable-next-line no-console
            console.warn('A client has not been set. Call the function with a clientID');
            return;
        }
        try {
            setLoading(true);
            const newAccessToken = await authService.getTokenForMerchant(clientToRefresh);
            setAccessToken(newAccessToken);
            setClientID(clientToRefresh);
            setLoading(false);
        }
        catch (error) {
            setLoading(false);
            throw new Error(`An error occurred while updating the token: ${error}`);
        }
    }, [authService, clientID]);
    const { cko_entity_segments: entitySegments = {}, cko_permissions: permissions = [], cko_client_id: clientId = undefined, cko_sub_entity_id: subEntityId = undefined, cko_user_id: userId = '', cko_user_roles: userRoles = [], cko_user_type: userType = 'External', sub = '', } = parseToken(accessToken || '');
    let isSegmentsOnlyUser = true;
    if (Object.keys(entitySegments).length === 0) {
        isSegmentsOnlyUser = false;
    }
    else {
        // check if any entity has a "*" in which case the user has full access to that entity.
        Object.values(entitySegments).forEach((segments) => {
            if (!Array.isArray(segments) && segments === '*') {
                isSegmentsOnlyUser = false;
            }
        });
    }
    // While the entities array does not exist in the token, this enables backwards compatibility.
    const getEntityIdsFromEntitySegments = (entitySegmentsFromToken) => {
        if (!entitySegmentsFromToken) {
            return [];
        }
        const entityIds = Object.keys(entitySegmentsFromToken);
        if (entityIds.length === 0) {
            return [];
        }
        return entityIds;
    };
    const entities = getEntityIdsFromEntitySegments(entitySegments);
    const contextValues = useMemo(() => ({
        accessToken,
        tokenProperties: {
            clientId,
            entities,
            entitySegments,
            permissions,
            sub,
            subEntityId,
            userId,
            userRoles,
            userType,
        },
        loading,
        isSegmentsOnlyUser,
        actions: {
            refreshToken,
        },
    }), [
        accessToken,
        clientId,
        entities,
        entitySegments,
        isSegmentsOnlyUser,
        loading,
        permissions,
        refreshToken,
        sub,
        subEntityId,
        userId,
        userRoles,
        userType,
    ]);
    return (React.createElement(ClientTokenContext.Provider, { value: contextValues }, children));
};
